// tinker: grid — feel strong vs weak. A 16-step row pulses at the current
// step while a minimal groove loops (config.pattern names a DRUM_PATTERNS
// starter, config.bpm sets the tempo). Strong beats (steps 0, 4, 8, 12) sit
// visually heavier; tapping any step drops a hat accent onto it live, so the
// learner hears what landing ON the beat vs BETWEEN the beats feels like.
// One idea only — no checks, no keeper, always ready to continue.
//
// Audio can't start without a user gesture, so a begin/stop toggle gates the
// transport; the groove (and its accents) stops on unmount.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DRUM_PATTERNS,
  STEPS_PER_BAR,
  playHat,
  startGroove,
} from "../../../lib/homeKeyGroove";
import { resumeAudio } from "../../../lib/homeKeyAudio";

const STRONG_STEPS = [0, 4, 8, 12];

export default function TinkerGrid({ moment, onReadyChange }) {
  const config = moment.config || {};
  const patternKey = config.pattern;
  const base = useMemo(
    () => DRUM_PATTERNS[patternKey] || DRUM_PATTERNS.heartbeat || {},
    [patternKey]
  );
  const bpm = config.bpm || 92;
  const swing = config.swing || 0.5;

  const [accents, setAccents] = useState(() => new Set());
  const [playing, setPlaying] = useState(false);
  const [now, setNow] = useState(-1);

  // The transport reads this one object; keeping its identity stable and
  // reassigning its arrays lets accent taps land in the loop live.
  const liveRef = useRef({ ...base, hat: [...(base.hat || [])] });
  const grooveRef = useRef(null);

  useEffect(() => {
    if (onReadyChange) onReadyChange(true);
  }, [onReadyChange]);

  const applyAccents = useCallback(
    (accentSet) => {
      const hat = new Set(base.hat || []);
      accentSet.forEach((s) => hat.add(s));
      liveRef.current.hat = [...hat].sort((a, b) => a - b);
    },
    [base]
  );

  const stopGroove = useCallback(() => {
    if (grooveRef.current && grooveRef.current.stop) grooveRef.current.stop();
    grooveRef.current = null;
    setPlaying(false);
    setNow(-1);
  }, []);
  useEffect(() => () => stopGroove(), [stopGroove]);

  const toggleGroove = () => {
    if (grooveRef.current) {
      stopGroove();
      return;
    }
    resumeAudio();
    applyAccents(accents);
    grooveRef.current = startGroove({
      pattern: liveRef.current,
      bpm,
      swing,
      onStep: (step) => setNow(step % STEPS_PER_BAR),
    });
    setPlaying(true);
  };

  const tapStep = (step) => {
    resumeAudio();
    const next = new Set(accents);
    if (next.has(step)) next.delete(step);
    else {
      next.add(step);
      if (!grooveRef.current) playHat(0); // a taste while the groove is off
    }
    applyAccents(next);
    setAccents(next);
  };

  // Steps where the base groove already lands something — shown as seeds so
  // the pulse is visible even before play.
  const seeded = useMemo(() => {
    const s = new Set();
    ["kick", "snare", "hat", "hatOpen"].forEach((row) =>
      (base[row] || []).forEach((step) => s.add(step))
    );
    return s;
  }, [base]);

  const steps = Array.from({ length: STEPS_PER_BAR }, (_, i) => i);

  return (
    <div className="hk-moment-body">
      <p>
        {config.prompt ||
          "A bar of music is a grid of 16 little steps — but they aren't equal. Steps 1, 5, 9, and 13 are the strong beats; the rest live in between. Start the groove, then tap steps to sprinkle hats on them. Feel how ON the beat and BETWEEN the beats land differently."}
      </p>

      {!playing ? (
        <button type="button" className="hk-btn hk-btn--primary" onClick={toggleGroove}>
          begin — start the groove
        </button>
      ) : (
        <button type="button" className="hk-btn hk-btn--ghost" onClick={toggleGroove}>
          ■ stop the groove
        </button>
      )}

      <div className="hk-step-grid" role="group" aria-label="one bar of 16 steps">
        <div className="hk-drum-row">
          <span className="hk-drum-row-label">steps</span>
          <div className="hk-step-row">
            {steps.map((step) => {
              const strong = STRONG_STEPS.includes(step);
              const on = accents.has(step);
              return (
                <button
                  key={step}
                  type="button"
                  className={`hk-step${strong ? " hk-step--strong" : ""}${
                    on ? " hk-step--on" : ""
                  }${seeded.has(step) ? " hk-step--seed" : ""}${
                    now === step ? " hk-step--now" : ""
                  }`}
                  aria-pressed={on}
                  aria-label={`step ${step + 1}${strong ? " (strong beat)" : ""}${
                    on ? ", hat accent on" : ""
                  }`}
                  onClick={() => tapStep(step)}
                />
              );
            })}
          </div>
        </div>
      </div>

      <p className="hk-roll-caption">
        heavier squares = strong beats. Dots mark where the groove already lands.
      </p>
    </div>
  );
}
